/**
 * Lightweight inline sparkline — no recharts dependency.
 *
 * Used in KPI cards and tables where rendering 6 chart instances per
 * row is too expensive.  Pure SVG path with smooth quadratic curves.
 */
interface SparklineProps {
  values: readonly number[];
  height?: number;
  stroke?: string;
  fill?: string;
  ariaLabel?: string;
}

export function Sparkline({
  values,
  height = 36,
  stroke = "var(--c-primary-300)",
  fill = "rgba(45, 181, 224, 0.12)",
  ariaLabel,
}: SparklineProps): JSX.Element | null {
  if (values.length === 0) return null;
  const width = 120;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = width / Math.max(1, values.length - 1);
  const points = values.map((v, i) => ({
    x: i * stepX,
    y: height - ((v - min) / span) * (height - 4) - 2,
  }));
  const path = points
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(" ");
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
      preserveAspectRatio="none"
    >
      <path d={area} fill={fill} stroke="none" />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
}
